// @givmo/compliance — what one filing's panel says (CCM-R1-INTAKE (a); frames 18–20; DESIGN-NOTE-1b
// §4 and round 6). Pure functions over decoded records; no React, no reads.
//
// ══ THE BASIS LINE ═════════════════════════════════════════════════════════════════════════════
//
// Under a worked-out date the panel says what the date is counted from, in the organization's terms:
// "6 months after your fiscal year ends (June 30)". The words are the frames' (18, 19, 09) and the
// approved list for the shapes no frame shows (copy ruling 2026-09-30 18:00 PDT, item 2). The
// numbers come from the rule's own deadline entry as served (`source_deadline`), never from the
// browser's arithmetic: nothing here computes a date. A shape with no approved words gets no line
// rather than invented ones. A date the entry rolls to a business day (`business_day`, CCM-ST2-WA2)
// says which: the counted day alone would be untrue whenever the roll moved it.
//
// ══ THE YEARS ══════════════════════════════════════════════════════════════════════════════════
//
// History is one tile per filing period, newest first, titled by the period the filing COVERS (round
// 6): the year a fiscal or reporting year ends in, with "Fiscal year ended …" when it does not end on
// December 31; a licence's term; a one-time filing's year filed. Each tile reads its period's own
// record first and the obligation ROW only for the period no cycle carries (design go D1). "Nothing
// recorded for this year", never "Not filed": Givmo knows only what was recorded (plan §1.7).

import {
  displayComplianceDate,
  isReceiptBacked,
  localDayOf,
  type ComplianceArtifact,
  type ComplianceObligation,
  type ComplianceOccurrence,
  type ComplianceProfile,
  type DeadlineExtension,
  type FirstPeriod,
  type ObligationArtifact,
} from './compliance-decode';
import {
  isCycleFiled,
  type CycleDocument,
  type CycleDocumentRole,
  type OwnFiling,
  type OwnFilingCadence,
  type OwnFilingCycle,
  type RenewalCycle,
} from './compliance-filings-decode';
import {
  anchorKindFor,
  closedByTheRow,
  coveringYear,
  laterAct,
  type RecordedAct,
  windowClosedByItsOwnRecord,
} from './compliance-filings-model';
import type { SubmissionHandoff } from './compliance-submission-decode';

// ── Dates ──────────────────────────────────────────────────────────────────────────────────────

/** "December 31, 2026": the panel's lead date (frames 18–20). */
export function longComplianceDate(iso: string | undefined): string {
  if (iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "June 30": a month and day without a year (a fiscal year end, a licence period's end). */
export function monthDay(iso: string | undefined): string | undefined {
  if (iso === undefined || iso === '') return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' });
}

// ── The basis line ─────────────────────────────────────────────────────────────────────────────

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function intOf(entry: Record<string, unknown>, key: string): number | undefined {
  const value = entry[key];
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

/**
 * The business-day roll a deadline entry records (`business_day.roll`, CCM-ST2-WA2): `preceding`,
 * the last business day on or before the counted day, or `following`, the next one on or after it.
 * `undefined` when the entry records none; `null` when it records one this page has no words for.
 */
function businessDayRoll(
  entry: Record<string, unknown>,
): 'preceding' | 'following' | null | undefined {
  const value = entry.business_day;
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'object' || Array.isArray(value)) return null;
  const roll = (value as Record<string, unknown>).roll;
  return roll === 'preceding' || roll === 'following' ? roll : null;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface BasisInput {
  dueDateBasis: string;
  /** The rule's own deadline entry, as served on the cycle. */
  sourceDeadline: Record<string, unknown>;
  /** The organization's fiscal year end as recorded ("June 30"), where one is. */
  fiscalYearEnd?: string;
  jurisdictionName: string;
  /** The end of the licence period a report counts from ("March 31"), where one is recorded. */
  licencePeriodEnd?: string;
}

/**
 * What a library filing's date is counted from, in the frames' words. `undefined` where the shape has
 * no approved words (then the panel shows no line rather than invented ones).
 */
export function libraryBasisLine(input: BasisInput): string | undefined {
  const entry = input.sourceDeadline;
  const fye = input.fiscalYearEnd !== undefined ? ` (${input.fiscalYearEnd})` : '';
  switch (input.dueDateBasis) {
    case 'fiscal_year_end_plus_interval': {
      const months = intOf(entry, 'months_after_fiscal_year_end');
      const days = intOf(entry, 'days_after_fiscal_year_end');
      const parts = [
        ...(months !== undefined && months > 0 ? [count(months, 'month', 'months')] : []),
        ...(days !== undefined && days > 0 ? [count(days, 'day', 'days')] : []),
      ];
      if (parts.length === 0) return undefined;
      return `${parts.join(' and ')} after your fiscal year ends${fye}`;
    }
    case 'fiscal_year_end_nth_month_day': {
      const day = intOf(entry, 'day_of_month');
      const month = intOf(entry, 'month_offset_after_fiscal_year_end');
      if (day === undefined || month === undefined) return undefined;
      const roll = businessDayRoll(entry);
      if (roll === null) return undefined;
      /*
       * B1, APPROVED (D-CCM-104, Jonathan 2026-10-17 15:47 PDT, "approve"; CCM-ST2-BUSINESS-DAY-BASIS-LINE):
       * a date on a month's last business day. Washington's renewal, "the last business day of the
       * eleventh month" (WAC 434-120-140(2)(a); CCM-ST2-WA2-B): day 31, rolled back past a weekend
       * or a holiday its rule lists. W1's "Last day of the …" would be untrue whenever the roll
       * moved the date (May 28, 2027, not May 31).
       */
      if (roll === 'preceding') {
        return day === 31
          ? `Last business day of the ${ordinal(month)} calendar month after your fiscal year ends${fye}`
          : undefined;
      }
      // CCM-ST2-TN-REG-DATE, W1 (approved, D-CCM-64; item 37): day 31 is every month's last
      // day (the backend clamps it to a shorter month's), so it is said as the last day, never as a
      // "31st day" a June or a February doesn't have. Tennessee's registration is the first rule to
      // carry it (§ 48-101-506(d), "the last day of the sixth month").
      const counted =
        day === 31
          ? `Last day of the ${ordinal(month)} calendar month after your fiscal year ends${fye}`
          : `${ordinal(day)} day of the ${ordinal(month)} calendar month after your fiscal year ends${fye}`;
      /*
       * B2, APPROVED (D-CCM-104, Jonathan 2026-10-17 15:47 PDT, "approve"; CCM-ST2-BUSINESS-DAY-BASIS-LINE):
       * a date that moves on to the next business day. Minnesota's annual report, postmarked by the
       * 15th day of the seventh month or, on "a Saturday, Sunday, or federal holiday", "by the next
       * business day" (C2; CCM-ST2-MN2). B2 was approved on a counted day only: on day 31, W1's
       * "Last day of the …" with B2's ending is a composition no ruling approved (cut 1008-87's
       * review, F4), so that roll has no line, as a preceding roll on another day has none.
       */
      if (roll === 'following') {
        return day === 31 ? undefined : `${counted}, or the next business day`;
      }
      return counted;
    }
    case 'registration_period_end_plus_interval': {
      const days = intOf(entry, 'days_after_period_end');
      if (days === undefined) return undefined;
      const end = input.licencePeriodEnd !== undefined ? ` (${input.licencePeriodEnd})` : '';
      return `${count(days, 'day', 'days')} after your license period ends${end}`;
    }
    case 'license_cycle_expiry':
      return 'When your license expires';
    case 'fixed_calendar_date': {
      const month = intOf(entry, 'month');
      const day = intOf(entry, 'day_of_month');
      if (month === undefined || day === undefined || month < 1 || month > 12) return undefined;
      return `Each year on ${MONTH_NAMES[month - 1]} ${day}`;
    }
    case 'concurrent_with_federal_return':
      return 'With your federal annual return';
    case 'not_stated_in_source':
      return 'Recurs, but the source gives no date';
    case 'before_first_solicitation':
      return `Before you first solicit in ${input.jurisdictionName}`;
    case 'last_day_of_anniversary_month':
      return 'By the end of the month your registration’s anniversary falls in';
    default:
      return undefined;
  }
}

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * A month and day as the backend records one ("06-30": the `month_day` value kind, `MM-DD`), in the
 * frames' words ("June 30"). Anything else reads as nothing: a recorded value is never shown as the
 * wire spells it.
 */
export function monthDayWords(recorded: string | undefined): string | undefined {
  const match = /^(\d{2})-(\d{2})$/.exec(recorded?.trim() ?? '');
  if (match === null) return undefined;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const name = MONTH_NAMES[month - 1];
  if (name === undefined || day < 1 || day > (DAYS_IN_MONTH[month - 1] ?? 0)) return undefined;
  return `${name} ${day}`;
}

/** The organization's fiscal year end as its profile records it, in words ("June 30"), where it does. */
export function recordedFiscalYearEnd(profile: ComplianceProfile | undefined): string | undefined {
  return monthDayWords(
    profile?.parameters.find(
      (parameter) =>
        parameter.parameterKey === 'fiscal_year_end' && parameter.status !== 'superseded',
    )?.recordedValue,
  );
}

/** The fiscal year end a year was worked out from, or the one on record, in words. */
export function fiscalYearEndFor(
  cycle: RenewalCycle | undefined,
  recorded: string | undefined,
): string | undefined {
  const fromCycle = cycle?.computedFrom.find(
    (parameter) => parameter.parameterKey === 'fiscal_year_end',
  )?.recordedValue;
  return monthDayWords(fromCycle) ?? recorded;
}

/**
 * What one library year's date is counted from, as the panel and the Calendar both say it: the
 * rule's deadline entry, the fiscal year end it was worked out from, the licence's period end.
 */
export function cycleBasisLine(
  obligation: ComplianceObligation,
  cycle: RenewalCycle | undefined,
  recordedFye: string | undefined,
): string | undefined {
  if (cycle === undefined) return undefined;
  return libraryBasisLine({
    dueDateBasis: cycle.dueDateBasis,
    sourceDeadline: cycle.sourceDeadline,
    fiscalYearEnd: fiscalYearEndFor(cycle, recordedFye),
    jurisdictionName: obligation.jurisdictionName || obligation.jurisdictionCode,
    licencePeriodEnd: monthDay(obligation.licenseExpiresOn),
  });
}

/** What an own filing's date is counted from: the organization's own cadence (frame 20; item 2). */
export function ownBasisLine(
  cadence: OwnFilingCadence | undefined,
  interval: number | undefined,
): string | undefined {
  switch (cadence) {
    case 'fixed_date_yearly':
      return 'Each year on the date you set';
    case 'one_time':
      return 'On the date you set';
    case 'every_n_months':
      return interval !== undefined ? `Every ${interval} months from the date you set` : undefined;
    case 'every_n_years':
      return interval !== undefined ? `Every ${interval} years from the date you set` : undefined;
    default:
      return undefined;
  }
}

/** What a basis line's "Change" changes, or nothing. */
export type BasisChange = 'fiscal-year-end' | 'license-expiry' | 'registration-date' | 'own-date';

/** A date only the organization has: a licence's expiry, a registration date, its own date. */
export type AnchorDateKind = 'license-expiry' | 'registration-date' | 'own-date';

/**
 * Each of those dates by name, spelled once: the date field's label, and the first line of a change to
 * it in "Corrections and changes" (CCM-R1-INTAKE-HISTORY).
 */
export const ANCHOR_LABELS: Readonly<Record<AnchorDateKind, string>> = {
  'license-expiry': 'License expiration date',
  'registration-date': 'Registration date',
  'own-date': 'Next date',
};

export function basisChangeFor(dueDateBasis: string): BasisChange | undefined {
  switch (dueDateBasis) {
    case 'fiscal_year_end_plus_interval':
    case 'fiscal_year_end_nth_month_day':
    case 'concurrent_with_federal_return':
      return 'fiscal-year-end';
    case 'license_cycle_expiry':
    case 'registration_period_end_plus_interval':
      return 'license-expiry';
    case 'anniversary_of_registration':
    case 'last_day_of_anniversary_month':
      return 'registration-date';
    default:
      return undefined;
  }
}

// ── The date block's words, spelled once (the panel and the add sheet both say them) ─────────────

/** The lead of a date block with no date yet (frame 04; the panel's questions). */
export const NOT_WORKED_OUT_YET = 'Not worked out yet';

/**
 * Frame 04's two lines (Jonathan's ruling, 2026-09-30 22:46 PDT, put them in the panel's expiry
 * question; CCM-R1-INTAKE-NEXTDUE puts them back on the add sheet, as frame 04 shows): what happens
 * until the date is added, under every date question since rulings log item 13 (2026-10-01); and,
 * where the license itself is on file, why it is still asked for.
 */
export const UNTIL_THE_DATE_LINE =
  'Until you add the date, it stays on your calendar as a question.';
export const LICENSE_ATTACHED_NOTE =
  'You attached the license, but Givmo doesn’t read documents yet, so add the date here.';

/**
 * Whether {@link LICENSE_ATTACHED_NOTE} belongs to a filing: D.C.'s licence-period rules, the licence
 * frame 04's approved words were written for — on the add sheet and the panel alike, so the two never
 * disagree. Georgia's registration has the same deadline shape and is not a licence; D.C.'s report
 * runs from the licence without being one; another jurisdiction's licence would need its own words
 * approved (the orchestrator's fold call, cut 1001-11, 2026-10-01 23:08 PDT).
 *
 * ⚠ Keyed on the jurisdiction because nothing the reads serve says "a licence" today.
 * `CCM-R1-INTAKE-NEXTDUE-ALIGN` replaces this with a served discriminator (the rule's fees on the
 * filing options read: a fee charged per licence cycle, `license_cycle`, is a licence's).
 */
export function takesLicenseNote(jurisdictionCode: string, dueDateBasis: string): boolean {
  return jurisdictionCode === 'DC' && dueDateBasis === 'license_cycle_expiry';
}

/**
 * L1, APPROVED (D-CCM-71, Jonathan 2026-10-13 08:54 PDT, "approve recs"; CCM-R1-REVIEW-N41-DATELESS):
 * a library filing with no date, on its panel and in the Calendar's "Not supported yet", and on the
 * add sheet's card too (the orchestrator's Q1, 2026-10-13 12:32 PDT). These are N51's words. The
 * clause "so it isn’t on your calendar" is gone: the approved calendar lines put such a filing on the
 * calendar. No sentence goes under it (Jonathan's ruling, 2026-09-30).
 */
export const NO_DATE_YET_LINE = 'Givmo doesn’t have a date for this yet.';

// ── A first year that counts from another filing's sending (D-CCM-110 J-F; the served
// `first_period`, CCM-R1-MD-ANNUAL-REPORT-FIRST-YEAR). Maryland's annual report is the one today: its
// first year is the first fiscal year that ends after the registration statement was sent. ──────
/**
 * C1-a, APPROVED (D-CCM-112, Jonathan 2026-10-18 18:13 PDT, "approve recs"): the Filings list's Next cell,
 * and the comparison's Now.
 */
export const FIRST_PERIOD_WAITS_CELL = 'Waits on your registration statement';
/**
 * C1-b, APPROVED (D-CCM-112, Jonathan 2026-10-18 18:13 PDT, "approve recs"): the panel's and the add sheet's
 * line under "Not worked out yet".
 */
export function firstPeriodWaitsLine(jurisdiction: string): string {
  return `Givmo counts ${jurisdiction}’s first annual report from the first fiscal year that ends after your registration statement is sent. Once that’s recorded on ${jurisdiction}’s registration, Givmo works out the dates.`;
}
/**
 * C1-c, APPROVED (D-CCM-112, Jonathan 2026-10-18 18:13 PDT, "approve recs"): the Calendar's question line;
 * its button opens the filing.
 */
export function firstPeriodWaitsQuestion(jurisdiction: string): string {
  return `Givmo works out the dates once your registration statement is recorded as sent on ${jurisdiction}’s registration.`;
}
/**
 * F1, APPROVED (D-CCM-112, Jonathan 2026-10-18 18:13 PDT, "approve recs"): under a first year's date, on
 * the review's card and the panel.
 */
export function firstPeriodLine(jurisdiction: string): string {
  return `Givmo counts ${jurisdiction}’s first annual report from the first fiscal year that ends after your registration statement was sent.`;
}

/** A duty due before the organization first solicits in a jurisdiction. */
export function beforeFirstSolicitLine(jurisdictionName: string): string {
  return `Before you first solicit in ${jurisdictionName}`;
}

/**
 * N1, APPROVED (D-CCM-56, Jonathan 2026-10-09 00:43 PDT, "go with your recs"; CCM-R1-INTAKE-MDEXT-OPEN):
 * beside a statutory date that has passed while the year's automatic extension runs, with nothing
 * recorded for it. The extension line under it says the rest, in the source's words.
 */
export const DATE_PASSED = 'Date passed';

/**
 * L1, APPROVED (D-CCM-62, Jonathan 2026-10-12 14:03 PDT, "agree"; CCM-R1-LATE-FILINGS): a year whose
 * window, counting its automatic extension, closed with nothing recorded for it — the heading of its
 * card on the filing's panel, the heading of a group at the top of the Calendar, and under its date on
 * Filings. Always beside its date and its period's "Nothing recorded" line (`nothingRecordedLine`;
 * `overdueCycles`).
 */
export const OVERDUE = 'Overdue';

/** The approved line on a year with nothing recorded for it (the panel's tiles say it too). */
export const NOTHING_RECORDED_FOR_THIS_YEAR = 'Nothing recorded for this year.';

/**
 * C1, APPROVED (D-CCM-85, Jonathan 2026-10-14 15:49 PDT, "approve recs"; CCM-BE24-B-COVERED-YEARS):
 * after "It was wrong", a year a filing recorded under the earlier year end covers more than half of
 * (D-CCM-81 (B)), on that year's History tile in place of {@link NOTHING_RECORDED_FOR_THIS_YEAR}, with
 * no "Add it" (Q1). The date is the covering year's end, in the tile's own form.
 */
export function coveredYearLine(coveringPeriodEnd: string): string {
  return `Covered by your filing for the fiscal year ended ${displayComplianceDate(coveringPeriodEnd)}.`;
}

/**
 * (b), APPROVED (D-CCM-76, Jonathan 2026-10-13 12:12 PDT, "approve recs"; CCM-R1-LATE-FILINGS-WORDS):
 * a license period with nothing recorded for it, on its overdue card and its History tile, and in the
 * Calendar's Overdue group, the sibling site (the orchestrator's Q2, 2026-10-13 12:32 PDT). The card
 * keeps no period line, since the tile's title names the years.
 */
export const NOTHING_RECORDED_FOR_THIS_LICENSE_PERIOD = 'Nothing recorded for this license period.';

/**
 * What a period with nothing recorded for it says. A license period is a cycle on either
 * license-period basis (the backend's `_LICENCE_PERIOD_BASES`; `anchorKindFor`), keyed by its term,
 * never a year, and takes (b)'s line. A fiscal year, an own filing's period and a filing with no
 * cycle (`undefined`) keep the year's.
 */
export function nothingRecordedLine(
  cycle: Pick<RenewalCycle, 'cycleKey' | 'dueDateBasis'> | undefined,
): string {
  return cycle !== undefined &&
    anchorKindFor(cycle.dueDateBasis) === 'license_expiry' &&
    !isYearKeyed(cycle.cycleKey)
    ? NOTHING_RECORDED_FOR_THIS_LICENSE_PERIOD
    : NOTHING_RECORDED_FOR_THIS_YEAR;
}

/**
 * L4, APPROVED (D-CCM-62): the lead of a late fee the rule's own source states, under a year read
 * overdue; the source's words, quoted, and its citation follow. No amount is worked out.
 */
export function lateFeeLead(jurisdictionName: string): string {
  return `Late fee, as ${jurisdictionName}’s source states it:`;
}

/**
 * L6, APPROVED (D-CCM-62): under the form prepared for a year before the current one. The form catalog
 * keeps the template Givmo took and the day it took it (`templateAccessedOn`), and ties no version to
 * a year.
 */
export function earlierYearFormLine(jurisdictionName: string, takenOn: string): string {
  return `This is ${jurisdictionName}’s form as Givmo took it on ${takenOn}. Givmo doesn’t know which version ${jurisdictionName} expects for an earlier year.`;
}

/**
 * APPROVED (D-CCM-63, Jonathan 2026-10-12 14:38 PDT, "approve recs"; CCM-R1-ADD-SHEET-WHICH-YEAR): the
 * add sheet's question where the entered last filing's date has no on-time reading (REVIEW-27's 3a),
 * its choices the years named as History names them.
 */
export const WHICH_YEAR_QUESTION = 'Which year was this filing for?';

/** "If not filed by then, it’s automatically extended to …" — only with its condition (Q6). */
export function extensionLine(extension: DeadlineExtension | undefined): string | undefined {
  if (extension === undefined) return undefined;
  return `If not filed by then, it’s automatically extended to ${displayComplianceDate(extension.extendedDueOn)} ${extension.condition}.`;
}

// ── What a recorded filing is, on the wire (design go D3) ────────────────────────────────────────

/**
 * Which receipt kind a confirmed filing records, now that the form no longer asks (frames 03, 04,
 * 18b): an exemption is an exemption filing; a recurring year is a renewal; a first or one-time
 * filing of a registration, conditional registration or foreign qualification is a registration.
 * Invisible here, but it names the act on the Board feed.
 */
export function receiptKindFor(
  obligationClass: string,
  recurring: boolean,
): 'registered_filed' | 'exempt_filed' | 'renewed_filed' {
  if (obligationClass === 'exemption') return 'exempt_filed';
  if (recurring) return 'renewed_filed';
  return 'registered_filed';
}

/**
 * Whether a cycle is one of a recurring filing's years (its key is a year), as against a standing
 * one. CCM-BE24-B: a short year a changed fiscal year end left keeps "YYYY-short" where the year it
 * ends in is already an earlier year's ("2026" and "2026-short"); a corrected year whose key a year
 * with something recorded holds keeps "YYYY-corrected" ("2025" and "2025-corrected", then
 * "2025-corrected-2"). Each is a year all the same.
 */
export function isYearKeyed(cycleKey: string): boolean {
  return /^\d{4}(-short(-\d{4})?|-corrected(-\d+)?)?$/.test(cycleKey);
}

/** The calendar year a year-keyed cycle's key names: 2026 for "2026", "2026-short" and "2026-corrected". */
export function yearOfCycleKey(cycleKey: string): number {
  return Number.parseInt(cycleKey.slice(0, 4), 10);
}

/**
 * "Add an earlier year"'s years for a library filing (copy ruling item 7): each year below the latest
 * it holds, back to ten years before its earliest, that it doesn't hold. Where its first year counts
 * from another filing's sending (D-CCM-110 J-F, the served `first_period`), none ending on or before
 * the earlier of the day that filing was sent and the end of its earliest current year, the bound the
 * backend's door keeps: shown with nothing on it, a year before the first year would say it's due
 * (review-191, F3). A year's end is counted in whole years from that earliest year's own end.
 */
export function earlierYearChoices(
  cycles: readonly RenewalCycle[],
  firstPeriod?: FirstPeriod,
): number[] {
  const heldYears = cycles
    .filter((cycle) => isYearKeyed(cycle.cycleKey))
    .map((cycle) => yearOfCycleKey(cycle.cycleKey));
  const held = new Set(heldYears);
  const latest = Math.max(...heldYears, 1900);
  const earliest = Math.min(...heldYears, latest);
  const from =
    firstPeriod === undefined
      ? undefined
      : cycles
          .filter(
            (cycle) =>
              cycle.isCurrent && /^\d{4}$/.test(cycle.cycleKey) && cycle.periodEnd !== undefined,
          )
          .sort((a, b) => yearOfCycleKey(a.cycleKey) - yearOfCycleKey(b.cycleKey))[0];
  const fromEnd = from?.periodEnd;
  const bound =
    fromEnd === undefined
      ? undefined
      : firstPeriod?.sentOn !== undefined && firstPeriod.sentOn < fromEnd
        ? firstPeriod.sentOn
        : fromEnd;
  const years: number[] = [];
  for (let candidate = latest - 1; candidate >= Math.max(earliest - 10, 1900); candidate -= 1) {
    if (held.has(candidate)) continue;
    if (
      from !== undefined &&
      fromEnd !== undefined &&
      bound !== undefined &&
      yearEndBefore(fromEnd, yearOfCycleKey(from.cycleKey) - candidate) <= bound
    )
      continue;
    years.push(candidate);
  }
  return years;
}

/** An ISO year end `years` whole years before `end`, February 29 clamped to the 28th. */
function yearEndBefore(end: string, years: number): string {
  const year = Number.parseInt(end.slice(0, 4), 10) - years;
  const monthDay = end.slice(4);
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return `${String(year).padStart(4, '0')}${monthDay === '-02-29' && !leap ? '-02-28' : monthDay}`;
}

/** "For the fiscal year ended Dec 31, 2026": the record form's period line, for a year-keyed cycle. */
export function periodLineOf(cycle: RenewalCycle | undefined): string | undefined {
  if (cycle?.periodEnd === undefined || cycle.cycleKey.startsWith('standing:')) return undefined;
  return `For the fiscal year ended ${displayComplianceDate(cycle.periodEnd)}`;
}

// ── Where the add sheet's "filing as sent" goes (design go D1(2)) ──────────────────────────────

/** Where a filing's copy as sent belongs once the filing is placed. */
export type FiledCopyHome =
  /** The year (or period) the server placed the record on: its own door. */
  | { kind: 'cycle'; cycleId: string }
  /** A duty made once: the obligation keeps its documents (CCM-R1-FILING-CYCLES-INITIAL). */
  | { kind: 'obligation' }
  /** Neither can be named, so nothing is attached (and the sheet says so). */
  | { kind: 'none' };

/**
 * The deadline shapes that make no durable cycle: a duty made once, whose documents the obligation
 * keeps. The backend's own list (`_NO_CYCLE_DEADLINE_BASES`), with a rule of no deadline at all.
 */
const ONE_TIME_DUTY_BASES: readonly string[] = [
  'before_first_solicitation',
  'campaign_end_plus_interval',
];

export interface FiledCopyHomeInput {
  obligationId: string;
  /**
   * CCM-R1-ADD-SHEET-WHICH-YEAR: the year the organization said the filing was for, where its date
   * had no on-time reading: the record went on that year's own door, so the copy goes there too.
   */
  filedFor?: { cycleKey: string; deadlineIndex: number };
  /** What the sheet recorded: a filing with its confirmation, or a send. */
  status: 'confirmed' | 'sent';
  /** The date the sheet recorded (the send's, for a send). */
  date: string;
  /** The filing's cycles and occurrences, READ BACK after the record was placed. */
  cycles: readonly RenewalCycle[];
  occurrences: readonly ComplianceOccurrence[];
  /** Whether the organization's fiscal year end is on record: without it nothing is worked out. */
  fiscalYearEndRecorded: boolean;
}

/**
 * Where the add sheet attaches the filing as sent (design go D1(2)). Only the server knows which
 * year a date belongs to (`filing_record_bearing_key`), so the sheet reads back where it put the
 * record and follows it:
 *
 *   • a send: the cycle the send was mirrored onto (it carries the sent date);
 *   • a confirmation: the cycle of the year whose occurrence bears the row's record — the same year
 *     whose tile shows that confirmation (D1's fallback, `libraryYearTiles`);
 *   • failing that, the obligation, but only for a duty made once: a rule with a deadline that makes
 *     no cycle, or no deadline at all (no occurrence and no cycle, with the year end on record). The
 *     backend refuses the obligation's door for a recurring rule ("This filing keeps its documents
 *     on each of its cycles"), so it is never knocked on for one;
 *   • otherwise nowhere. A send dated inside the period it reports on is kept on the row and
 *     mirrored onto no year, and an unplaced record names no cycle; guessing would put the copy on
 *     the wrong year.
 */
export function filedCopyHome(input: FiledCopyHomeInput): FiledCopyHome {
  const allCycles = input.cycles.filter((cycle) => cycle.obligationId === input.obligationId);
  // A retired cycle takes no filing as sent (the backend refuses it), so only current ones count.
  const cycles = allCycles.filter((cycle) => cycle.isCurrent);
  const occurrences = input.occurrences.filter(
    (occurrence) => occurrence.obligationId === input.obligationId,
  );
  let year: RenewalCycle[];
  if (input.filedFor !== undefined) {
    const named = input.filedFor;
    year = cycles.filter(
      (cycle) => cycle.cycleKey === named.cycleKey && cycle.deadlineIndex === named.deadlineIndex,
    );
  } else if (input.status === 'sent') {
    year = cycles.filter((cycle) => cycle.submissionRecordedOn === input.date);
  } else {
    const bearing = new Set(
      occurrences
        .filter((occurrence) => isReceiptBacked(occurrence.filingRecordState))
        .map((occurrence) => `${occurrence.cycleKey}|${occurrence.deadlineIndex}`),
    );
    year = cycles.filter((cycle) => bearing.has(`${cycle.cycleKey}|${cycle.deadlineIndex}`));
  }
  const only = year.length === 1 ? year[0] : undefined;
  if (only !== undefined) return { kind: 'cycle', cycleId: only.cycleId };
  const madeOnce =
    occurrences.some((occurrence) => ONE_TIME_DUTY_BASES.includes(occurrence.dueDateBasis)) ||
    (input.fiscalYearEndRecorded && occurrences.length === 0 && allCycles.length === 0);
  return madeOnce ? { kind: 'obligation' } : { kind: 'none' };
}

// ── The year tiles ─────────────────────────────────────────────────────────────────────────────

/** The document roles, in the order a tile lists them (frames 18–20). */
const ROLE_ORDER: readonly CycleDocumentRole[] = [
  'filed_copy',
  'confirmation',
  'submission_proof',
  'attachment',
];

/** The approved name of each role (round 6). */
export const DOCUMENT_ROLE_NAMES: Readonly<Record<CycleDocumentRole, string>> = {
  filed_copy: 'Filing as sent',
  confirmation: 'Confirmation',
  submission_proof: 'Proof of sending',
  attachment: 'Attachment',
};

export interface TileDocument {
  /** The vault document's id; for a filing document's version, the version's id. */
  artifactId: string;
  role: CycleDocumentRole;
  fileName: string;
  contentType: string;
  uploadedByUserId?: string;
  uploadedAt?: string;
  /**
   * A filing document's version (a file of a package the organization recorded sending): it
   * downloads by its version's logged read, never as a vault document (CCM-R1-SUBMIT-HANDOFF).
   */
  version?: { documentId: string; versionId: string };
}

/** Where a tile's record can be added to, when it has none. */
export type TileTarget =
  | { kind: 'renewal-cycle'; cycle: RenewalCycle }
  | { kind: 'own-cycle'; cycle: OwnFilingCycle }
  | { kind: 'obligation' };

export interface YearTile {
  key: string;
  title: string;
  /** "Fiscal year ended Jun 30, 2025", when a fiscal year does not end on December 31. */
  qualifier?: string;
  /** What was recorded, newest act first; `undefined` is its period's "Nothing recorded" line. */
  act?: RecordedAct;
  documents: TileDocument[];
  /** Who recorded and added what, by user id, and on which days. */
  addedBy: string[];
  addedOn: string[];
  /** Where "Add it" records, for a tile with nothing recorded. */
  target: TileTarget;
  /** C1 (`coveredYearLine`), on a year a filing recorded for another year covers. No "Add it". */
  covered?: string;
  /** The date the period's due date falls on, for ordering. */
  sortKey: string;
}

function yearOf(iso: string | undefined): string | undefined {
  return iso !== undefined && /^\d{4}/.test(iso) ? iso.slice(0, 4) : undefined;
}

function sortDocuments(documents: TileDocument[]): TileDocument[] {
  return [...documents].sort(
    (a, b) =>
      ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) ||
      a.fileName.localeCompare(b.fileName),
  );
}

function uniqueSorted(values: (string | undefined)[]): string[] {
  return [
    ...new Set(values.filter((value): value is string => value !== undefined && value !== '')),
  ].sort();
}

/** What names a year as History names it: its key, its period and its date. */
export interface NamedYear {
  cycleKey: string;
  periodStart?: string;
  periodEnd?: string;
  dueOn?: string;
  /** CCM-BE24-B: the short year a changed fiscal year end left, as the backend serves it. */
  shortYear?: true;
}

/**
 * CCM-BE24-B (D-CCM-74's Y6): the short year a changed fiscal year end left, from its first day, on
 * its tile after "Fiscal year ended {date}" and on its Overdue card after "For the fiscal year ended
 * {date}". Only where the backend serves the year as short, never worked out from its length.
 */
export function shortYearLine(cycle: NamedYear): string | undefined {
  return cycle.shortYear === true && cycle.periodStart !== undefined
    ? `A short year, from ${displayComplianceDate(cycle.periodStart)}`
    : undefined;
}

/** Lines that follow one another on one line, as a date and its note do (" · "). */
function oneLine(...parts: (string | undefined)[]): string | undefined {
  const present = parts.filter((part): part is string => part !== undefined);
  return present.length > 0 ? present.join(' · ') : undefined;
}

/**
 * The title and qualifier of a library filing's year (round 6's rule): History's tiles, and the add
 * sheet's "Which year was this filing for?" (CCM-R1-ADD-SHEET-WHICH-YEAR), name years alike.
 */
export function cycleTitle(cycle: NamedYear): { title: string; qualifier?: string } {
  const endYear = yearOf(cycle.periodEnd);
  if (cycle.cycleKey.startsWith('standing:')) {
    // A licence's term, where the period is served; else the year its date falls in.
    const startYear = yearOf(cycle.periodStart);
    if (startYear !== undefined && endYear !== undefined && startYear !== endYear) {
      return { title: `${startYear}–${endYear}` };
    }
    return { title: endYear ?? yearOf(cycle.dueOn) ?? '—' };
  }
  const title =
    endYear ??
    (isYearKeyed(cycle.cycleKey)
      ? String(yearOfCycleKey(cycle.cycleKey))
      : (yearOf(cycle.dueOn) ?? '—'));
  const qualifier = oneLine(
    cycle.periodEnd !== undefined && !cycle.periodEnd.endsWith('-12-31')
      ? `Fiscal year ended ${displayComplianceDate(cycle.periodEnd)}`
      : undefined,
    shortYearLine(cycle),
  );
  return { title, ...(qualifier !== undefined ? { qualifier } : {}) };
}

/**
 * "For the fiscal year ended January 31, 2026": a year's line on its Overdue card and, where two
 * years are open, on each Next due card (CCM-BE24-B), with Y6 after it on a short year.
 */
export function fiscalYearEndedLine(cycle: NamedYear): string | undefined {
  if (cycle.periodEnd === undefined || !isYearKeyed(cycle.cycleKey)) return undefined;
  return oneLine(
    `For the fiscal year ended ${longComplianceDate(cycle.periodEnd)}`,
    shortYearLine(cycle),
  );
}

/** A filing's act, with the close of the window it came after where its record serves one. */
function filedAct(on: string, afterWindowClosedOn: string | undefined): RecordedAct {
  return afterWindowClosedOn !== undefined
    ? { kind: 'filed', on, afterWindowClosedOn }
    : { kind: 'filed', on };
}

function cycleActOf(cycle: RenewalCycle): RecordedAct | undefined {
  if (isCycleFiled(cycle) && cycle.recordedFiledOn !== undefined) {
    // The year's own close (its due date, or its automatic extension), as the cycle serves it.
    return filedAct(cycle.recordedFiledOn, cycle.filedAfterWindowClosedOn);
  }
  if (cycle.submissionRecordedOn !== undefined)
    return { kind: 'sent', on: cycle.submissionRecordedOn };
  return undefined;
}

/**
 * The obligation row's own act. Its filing states the close it came after where the filing has no
 * years (`withClose`: a one-time filing, whose tile is the row's), and where the backend names the
 * year it was for (CCM-R1-WINDOW-LABEL-D: `filedAfterWindowClosedCycleId`, served beside the close),
 * on whose tile it lands. A late filing recorded on the row whose year is NOT named lands on a later
 * year's tile than the window it names (its own tile, titled by the year it was filed, or the next
 * period's), where the clause would contradict the year that reads "Nothing recorded for this year";
 * that tile reads "Filed {date}" instead, and the register's label still states both dates.
 */
function rowActOf(obligation: ComplianceObligation, withClose: boolean): RecordedAct | undefined {
  if (
    isReceiptBacked(obligation.filingRecordState) &&
    obligation.confirmationArtifactId !== undefined &&
    obligation.recordedFiledOn !== undefined
  ) {
    return filedAct(
      obligation.recordedFiledOn,
      withClose ? obligation.filedAfterWindowClosedOn : undefined,
    );
  }
  if (
    obligation.filingRecordState === 'submission_recorded' &&
    obligation.submissionRecordedOn !== undefined
  ) {
    return { kind: 'sent', on: obligation.submissionRecordedOn };
  }
  return undefined;
}

function documentFromCycle(document: CycleDocument): TileDocument {
  return {
    artifactId: document.artifactId,
    role: document.role,
    fileName: document.fileName,
    contentType: document.contentType,
    uploadedByUserId: document.uploadedByUserId,
    uploadedAt: document.uploadedAt,
  };
}

/** A document role this build knows; anything else is not shown under a guessed one. */
export function knownRole(role: string): CycleDocumentRole | undefined {
  return (ROLE_ORDER as readonly string[]).includes(role) ? (role as CycleDocumentRole) : undefined;
}

export interface LibraryTilesInput {
  obligation: ComplianceObligation;
  /** This obligation's cycles. */
  cycles: readonly RenewalCycle[];
  /** This obligation's occurrences: the year a row-level confirmation bears is told by them (D1). */
  occurrences: readonly ComplianceOccurrence[];
  /** The documents the obligation itself names (`…/obligations/{id}/artifacts`). */
  obligationDocuments: readonly ObligationArtifact[];
  /** The vault, for who added a document the obligation names. */
  vault: readonly ComplianceArtifact[];
  /** Today, as an ISO civil date. */
  today: string;
  /**
   * The zone the "Added … on …" days are read in: the viewer's own when absent (`localDayOf`'s one
   * rule, CCM-R1-INTAKE-DAY-BASIS). Tests name one to pin the evening case.
   */
  timeZone?: string;
}

/**
 * A library filing's year tiles, newest first.
 *
 * A year is shown when something was recorded for it, or when its date has passed with nothing
 * recorded ("Nothing recorded for this year"); a year still ahead with nothing recorded is the panel's
 * "Next due", not a tile. A retired period shows only where it holds a record. The obligation ROW's
 * own record becomes a tile of its own only where no year carries it (D1): the year whose occurrence
 * bears the row's confirmation, or — failing that, as for a one-time filing — the year it was filed.
 */
export function libraryYearTiles(input: LibraryTilesInput): YearTile[] {
  const { obligation } = input;
  // The row's filing dates its window on a filing with no years, or on the year the backend names
  // for it (`rowActOf`); that named year, else the year its occurrence bears, carries it.
  const named = obligation.filedAfterWindowClosedCycleId;
  const rowAct = rowActOf(
    obligation,
    input.cycles.length === 0 || input.cycles.some((cycle) => cycle.cycleId === named),
  );
  const closedOnTheRow = closedByTheRow(obligation, input.occurrences);
  const cycleArtifactIds = new Set(
    input.cycles.flatMap((cycle) => cycle.documents.map((document) => document.artifactId)),
  );
  const vaultById = new Map(
    input.vault.map((artifact) => [artifact.artifactId, artifact] as const),
  );
  const rowDocuments: TileDocument[] = input.obligationDocuments
    .filter((document) => document.isCurrent && !cycleArtifactIds.has(document.artifactId))
    .flatMap((document) => {
      const role = knownRole(document.referenceRole);
      if (role === undefined) return [];
      const vaulted = vaultById.get(document.artifactId);
      return [
        {
          artifactId: document.artifactId,
          role,
          fileName: document.fileName || vaulted?.fileName || '',
          contentType: document.contentType || vaulted?.contentType || '',
          uploadedByUserId: vaulted?.uploadedByUserId,
          uploadedAt: document.uploadedAt ?? vaulted?.uploadedAt,
        },
      ];
    });

  const tiles: YearTile[] = [];
  let rowPlaced = false;
  for (const cycle of input.cycles) {
    const own = cycleActOf(cycle);
    const bearsRow = own === undefined && rowAct !== undefined && closedOnTheRow(cycle);
    const act = own ?? (bearsRow ? rowAct : undefined);
    if (bearsRow) rowPlaced = true;
    // MDEXT-OPEN: a year with nothing recorded becomes "Nothing recorded for this year" once its
    // window closes, counting an automatic extension (served), or on its own date where the
    // organization recorded the window closed then; until then it is the "Next due".
    const closesOn = windowClosedByItsOwnRecord(obligation, cycle)
      ? cycle.dueOn
      : (cycle.windowClosesOn ?? cycle.dueOn);
    const past = cycle.dueOn !== undefined && closesOn !== undefined && closesOn < input.today;
    // CCM-BE24-B-COVERED-YEARS (D-CCM-85, Q5): a covered year is a tile as soon as it's covered,
    // whatever its window, as a recorded one is.
    const coveringEnd =
      act === undefined && cycle.isCurrent
        ? coveringYear(cycle, input.cycles)?.periodEnd
        : undefined;
    if (act === undefined && coveringEnd === undefined && (!cycle.isCurrent || !past)) continue;
    const documents = sortDocuments([
      ...cycle.documents.map(documentFromCycle),
      ...(bearsRow ? rowDocuments : []),
    ]);
    const { title, qualifier } = cycleTitle(cycle);
    tiles.push({
      key: cycle.cycleId,
      title,
      ...(qualifier !== undefined ? { qualifier } : {}),
      act,
      documents,
      addedBy: uniqueSorted([
        ...documents.map((document) => document.uploadedByUserId),
        cycle.recordedByUserId,
      ]),
      addedOn: uniqueSorted([
        ...documents.map((document) => localDayOf(document.uploadedAt, input.timeZone)),
        localDayOf(cycle.recordedAt, input.timeZone),
      ]),
      target: { kind: 'renewal-cycle', cycle },
      ...(coveringEnd !== undefined ? { covered: coveredYearLine(coveringEnd) } : {}),
      sortKey: cycle.periodEnd ?? cycle.dueOn ?? cycle.cycleKey,
    });
  }

  // The row's record, where no year carries it: placed by the year it was filed.
  const carried = tiles.some(
    (tile) =>
      tile.act !== undefined &&
      rowAct !== undefined &&
      tile.act.kind === rowAct.kind &&
      tile.act.on === rowAct.on,
  );
  if (rowAct !== undefined && !rowPlaced && !carried) {
    const documents = sortDocuments(rowDocuments);
    tiles.push({
      key: `row:${obligation.obligationId}`,
      title: yearOf(rowAct.on) ?? '—',
      act: rowAct,
      documents,
      addedBy: uniqueSorted(documents.map((document) => document.uploadedByUserId)),
      addedOn: uniqueSorted(
        documents.map((document) => localDayOf(document.uploadedAt, input.timeZone)),
      ),
      target: { kind: 'obligation' },
      sortKey: rowAct.on,
    });
  }

  return tiles.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
}

/** An own filing's period, as a tile: titled by the year its date falls in, with no qualifier. */
export function ownYearTiles(
  filing: OwnFiling,
  cycles: readonly OwnFilingCycle[],
  vault: readonly ComplianceArtifact[],
  today: string,
  /** As {@link LibraryTilesInput.timeZone}: the viewer's own zone when absent. */
  timeZone?: string,
): YearTile[] {
  const vaultById = new Map(vault.map((artifact) => [artifact.artifactId, artifact] as const));
  const named = (artifactId: string | undefined, role: CycleDocumentRole): TileDocument[] => {
    if (artifactId === undefined) return [];
    const vaulted = vaultById.get(artifactId);
    return [
      {
        artifactId,
        role,
        fileName: vaulted?.fileName ?? '',
        contentType: vaulted?.contentType ?? '',
        uploadedByUserId: vaulted?.uploadedByUserId,
        uploadedAt: vaulted?.uploadedAt,
      },
    ];
  };
  const tiles: YearTile[] = [];
  for (const cycle of cycles.length > 0
    ? cycles
    : filing.latestCycle !== undefined
      ? [filing.latestCycle]
      : []) {
    const act: RecordedAct | undefined =
      cycle.filed && cycle.recordedFiledOn !== undefined
        ? { kind: 'filed', on: cycle.recordedFiledOn }
        : cycle.submissionRecordedOn !== undefined
          ? { kind: 'sent', on: cycle.submissionRecordedOn }
          : undefined;
    const past = cycle.dueOn !== undefined && cycle.dueOn < today;
    if (act === undefined && !past) continue;
    const documents = sortDocuments([
      ...named(cycle.filedCopyArtifactId, 'filed_copy'),
      ...(cycle.filed ? named(cycle.confirmationArtifactId, 'confirmation') : []),
      ...named(cycle.submissionProofArtifactId, 'submission_proof'),
    ]);
    tiles.push({
      key: cycle.cycleId,
      title: yearOf(cycle.dueOn) ?? '—',
      act,
      documents,
      addedBy: uniqueSorted([
        ...documents.map((document) => document.uploadedByUserId),
        cycle.recordedByUserId,
      ]),
      addedOn: uniqueSorted([
        ...documents.map((document) => localDayOf(document.uploadedAt, timeZone)),
        localDayOf(cycle.recordedAt, timeZone),
      ]),
      target: { kind: 'own-cycle', cycle },
      sortKey: cycle.dueOn ?? '',
    });
  }
  return tiles.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
}

/** The latest act across a filing's tiles, for its lead. */
export function latestTileAct(tiles: readonly YearTile[]): RecordedAct | undefined {
  return tiles.reduce<RecordedAct | undefined>(
    (latest, tile) => laterAct(latest, tile.act),
    undefined,
  );
}

// ── A year filed after its window closed ───────────────────────────────────────────────────────
//
// Approved, D-CCM-46 (CCM-R1-WINDOW-LABEL-B; cut charity-compliance-1007-2's design go, Q1): a year
// tile's own act line, "Filed {date}", with the dated clause of rulings log item 29's second line
// (Jonathan, 2026-10-01 11:59 PDT: "… filed {filed date}, after the window closed {close date}").
// Jonathan chose it over the alternative, that ruled label whole on the tile. It states two
// dates and never "late": what a closed window means is the state's rule's to say. Shown only where
// the year's record serves the close (`afterWindowClosedOn`), and for a filing recorded on the row
// only where the filing has no years (`rowActOf`); every other filed year reads "Filed {date}" as
// before.

/** "Filed Apr 12, 2026, after the window closed Mar 31, 2026" (approved, D-CCM-46, above). */
export function filedAfterWindowClosedLine(filedOn: string, windowClosedOn: string): string {
  return `Filed ${displayComplianceDate(filedOn)}, after the window closed ${displayComplianceDate(windowClosedOn)}`;
}

// ── "Added by … on …" ──────────────────────────────────────────────────────────────────────────

function naturalList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

/**
 * "Dec 10 and Dec 12, 2025": the days, with a shared year said once (frame 18); days in different
 * years each keep their own.
 */
export function addedOnDays(days: readonly string[]): string {
  const sorted = [...days].sort();
  const years = new Set(sorted.map((day) => day.slice(0, 4)));
  if (years.size === 1 && sorted.length > 1) {
    const year = sorted[0]?.slice(0, 4) ?? '';
    const parts = sorted.map((day) => {
      const shown = displayComplianceDate(day);
      return shown.replace(`, ${year}`, '');
    });
    return `${naturalList(parts)}, ${year}`;
  }
  return naturalList(sorted.map((day) => displayComplianceDate(day)));
}

/**
 * "Added by Maya Solberg on Dec 10 and Dec 12, 2025" (frame 18). Names the host cannot resolve are
 * left out; with none left it reads "Added on …" (copy ruling item 8). Nothing at all without a day.
 */
export function addedByLine(
  tile: Pick<YearTile, 'addedBy' | 'addedOn'>,
  nameOf: (userId: string) => string | undefined,
): string | undefined {
  if (tile.addedOn.length === 0) return undefined;
  const names = [...new Set(tile.addedBy.map(nameOf).filter((name): name is string => !!name))];
  const days = addedOnDays(tile.addedOn);
  return names.length > 0 ? `Added by ${naturalList(names)} on ${days}` : `Added on ${days}`;
}

/** Month-name helper for an own filing's yearly date, kept beside the cadence words. */
export function ownCadenceMonthDay(filing: OwnFiling): string | undefined {
  if (filing.cadenceMonth === undefined || filing.cadenceDay === undefined) return undefined;
  const month = MONTH_NAMES[filing.cadenceMonth - 1];
  return month !== undefined ? `${month} ${filing.cadenceDay}` : undefined;
}

// ── What the organization recorded it sent itself, on its year (CCM-R1-SUBMIT-HANDOFF, D5) ─────

/**
 * The year tile of a filing the organization sent itself (frame 81). The register keeps the sent
 * date the record's mirror wrote, and the proof of sending, which its own documents carry; the
 * files that went are the record's: the package's signed form as "Filing as sent" and its
 * attachments as "Attachment", each a filing document's version, or the organization's own
 * document as "Filing as sent". They join the tile that carries the record's sent date, else the
 * case's own year, and add who recorded them, and when, to its "Added by" line.
 */
export function withRecordedFiles(
  tiles: readonly YearTile[],
  handoff: SubmissionHandoff | undefined,
  vault: readonly ComplianceArtifact[],
  options: { cycleId?: string; timeZone?: string } = {},
): YearTile[] {
  if (handoff === undefined) return [...tiles];
  let at = tiles.findIndex((tile) => tile.act?.kind === 'sent' && tile.act.on === handoff.sentOn);
  if (at < 0 && options.cycleId !== undefined) {
    at = tiles.findIndex((tile) => tile.key === options.cycleId);
  }
  const tile = at >= 0 ? tiles[at] : undefined;
  if (tile === undefined) return [...tiles];
  const files: TileDocument[] =
    handoff.content === 'package'
      ? handoff.package.flatMap((entry): TileDocument[] =>
          entry.role === 'unknown'
            ? []
            : [
                {
                  artifactId: entry.versionId,
                  role: entry.role === 'signed_form' ? 'filed_copy' : 'attachment',
                  fileName: entry.fileName,
                  contentType: '',
                  uploadedByUserId: handoff.recordedByUserId,
                  ...(handoff.recordedAt !== undefined ? { uploadedAt: handoff.recordedAt } : {}),
                  version: { documentId: entry.documentId, versionId: entry.versionId },
                },
              ],
        )
      : handoff.sentArtifactId !== undefined
        ? [
            {
              artifactId: handoff.sentArtifactId,
              role: 'filed_copy',
              fileName:
                vault.find((artifact) => artifact.artifactId === handoff.sentArtifactId)
                  ?.fileName ?? '',
              contentType: '',
              uploadedByUserId: handoff.recordedByUserId,
              ...(handoff.recordedAt !== undefined ? { uploadedAt: handoff.recordedAt } : {}),
            },
          ]
        : [];
  const held = new Set(tile.documents.map((document) => document.artifactId));
  const documents = sortDocuments([
    ...tile.documents,
    ...files.filter((file) => !held.has(file.artifactId)),
  ]);
  const joined: YearTile = {
    ...tile,
    documents,
    addedBy: uniqueSorted([...tile.addedBy, handoff.recordedByUserId]),
    addedOn: uniqueSorted([...tile.addedOn, localDayOf(handoff.recordedAt, options.timeZone)]),
  };
  return tiles.map((each, index) => (index === at ? joined : each));
}
